<template>
  <div class="form__item block-item review-section">
    <label class="form__label">
      <i v-if="icon" :class="'bi ' + icon" aria-hidden="true"></i>
      {{ label }}
    </label>

    <!-- Voice dictation. Only when editable.
         - @voice-start: snapshot the Quill content at session start.
         - @transcript: rebuild value as `<baseHtml><p>transcript</p>` so
           interim updates REPLACE rather than COMPOUND.
         - @voice-end: clear the snapshot so the next session starts from
           the now-committed content.
         This pattern keeps the editor's HTML clean and supports both
         single-shot and multi-session dictation per field. -->
    <div v-if="!readonly" class="voice-dictation-row">
      <VoiceDictationButton
        :label="`Dictate ${label.toLowerCase()}`"
        @voice-start="onVoiceStart"
        @transcript="onTranscript"
        @voice-end="onVoiceEnd"
      />
    </div>

    <div class="editor-wrapper" v-if="!readonly">
      <QuillEditor
        :content="modelValue || ''"
        @update:content="$emit('update:modelValue', $event)"
        contentType="html"
        :placeholder="placeholder"
      />
    </div>
    <div
      v-else
      class="readonly-content"
      v-html="modelValue || '<em>No comments</em>'"
    ></div>
  </div>
</template>

<script>
import { QuillEditor } from "@vueup/vue-quill";
import VoiceDictationButton from "@/components/VoiceDictationButton.vue";

export default {
  name: "ReviewSection",
  components: {
    QuillEditor,
    VoiceDictationButton,
  },
  props: {
    label: {
      type: String,
      required: true,
    },
    icon: {
      type: String,
      default: "",
    },
    modelValue: {
      type: String,
      default: "",
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: "",
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      // HTML snapshot of the field at the moment the current voice session
      // started. Null between sessions. Used so each interim transcript
      // event REPLACES into `baseHtml + <p>transcript</p>`, never compounds.
      voiceSessionBaseHtml: null,
    };
  },
  methods: {
    onVoiceStart() {
      // Snapshot the current Quill content. Subsequent @transcript events
      // append a single <p> after this baseline; @voice-end clears it.
      this.voiceSessionBaseHtml = this.modelValue || "";
    },
    onTranscript(text) {
      // Interim transcript update — rebuild value from the session snapshot
      // so successive interim events replace rather than compound the same
      // dictated phrase.
      if (!text) return;
      const base =
        this.voiceSessionBaseHtml !== null
          ? this.voiceSessionBaseHtml
          : this.modelValue || "";
      const escaped = String(text)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
      const html = base.trim()
        ? `${base}<p>${escaped}</p>`
        : `<p>${escaped}</p>`;
      this.$emit("update:modelValue", html);
    },
    onVoiceEnd() {
      // Session over — the next recording starts from whatever is now in
      // the editor (which includes the just-committed dictated text).
      this.voiceSessionBaseHtml = null;
    },
  },
};
</script>

<style scoped>
.review-section {
  background: #fafafa;
  border-radius: 8px;
  padding: 1rem;
  border-left: 4px solid var(--primary-color, #005596);
}

.review-section .form__label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: var(--primary-color, #005596);
  margin-bottom: 0.75rem;
}

.editor-wrapper {
  background: #fff;
  border-radius: 4px;
}

.voice-dictation-row {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.5rem;
}

.readonly-content {
  padding: 0.75rem;
  background: #fff;
  border-radius: 4px;
  border: 1px solid #e9ecef;
  min-height: 60px;
}

.overall-comments {
  border-left-color: var(--success-color, #28a745);
}

.overall-comments .form__label {
  color: var(--success-color, #28a745);
}
</style>
